// Content of the Help & guide screen: what each module is for, how work moves through it,
// and what every status means. Status names and badge colours come from constants/status.ts,
// so this guide always shows the same words as the rest of the app.
import { ALL, STAFF } from '../../../constants/permissions';
import { STOCK_REASONS, ATTENDANCE, INVOICE_STATUS, JOB_STATUS, PAYMENT_STATUS, PRIORITY, QUOTATION_STATUS, RECORD_STATUS } from '../../../constants/status';
import { PAY_TYPES, TXN_CATEGORIES } from '../../../constants/payments';
import type { Role } from '../../../constants/roles';
import type { LabelMap, StatusMap } from '../../../types/common';

/** One status (or type) and what it means. `next` says what usually happens after it. */
export interface GuideRow {
  value: string;
  meaning: string;
  next?: string;
}

/** A table of statuses: rows are looked up in `map` for their name (and badge, when `badges`). */
export interface GuideTable {
  title: string;
  map: StatusMap | LabelMap;
  badges?: boolean;
  rows: GuideRow[];
}

/** The usual path through a module: status keys in order, plus side steps that can happen. */
export interface GuideFlow {
  map: StatusMap;
  steps: string[];
  side?: { value: string; note: string }[];
}

export interface GuideSection {
  id: string;
  title: string;
  icon: string;
  /** Screen this section is about */
  to: string;
  roles: Role[];
  summary: string;
  flow?: GuideFlow;
  tables: GuideTable[];
  /** How it works, in order */
  howItWorks: string[];
  tips?: string[];
}

/** The big picture shown at the top: how the modules connect. */
export const BUSINESS_FLOWS: { title: string; roles: Role[]; steps: { label: string; section: string; note: string }[] }[] = [
  {
    title: 'From enquiry to money received',
    roles: STAFF,
    steps: [
      { label: 'Quotation', section: 'quotations', note: 'Price offer to the customer' },
      { label: 'Service job', section: 'jobs', note: 'The work on site' },
      { label: 'Invoice', section: 'invoices', note: 'The bill for the work' },
      { label: 'Payment', section: 'payments', note: 'Money received' },
    ],
  },
  {
    title: 'Material',
    roles: STAFF,
    steps: [
      { label: 'Purchase', section: 'purchases', note: 'Supplier bill recorded' },
      { label: 'Stock', section: 'purchases', note: 'Material in the store' },
      { label: 'Issued to job', section: 'jobs', note: 'Material used on site' },
    ],
  },
  {
    title: 'Team',
    roles: STAFF,
    steps: [
      { label: 'Attendance', section: 'salary', note: 'Who worked each day' },
      { label: 'Salary slip', section: 'salary', note: 'Pay worked out' },
      { label: 'Salary payment', section: 'payments', note: 'Money paid to staff' },
    ],
  },
  {
    title: 'Your work',
    roles: ['worker'],
    steps: [
      { label: 'Assigned', section: 'jobs', note: 'The office gives you a job' },
      { label: 'In progress', section: 'jobs', note: 'Tap Start work on site' },
      { label: 'Ready to invoice', section: 'jobs', note: 'Mark it when the work is done' },
    ],
  },
];

export const GUIDE: GuideSection[] = [
  {
    id: 'quotations',
    title: 'Quotations',
    icon: 'quote',
    to: '/quotations',
    roles: STAFF,
    summary: 'A price offer for the customer, with fitting and labour, material from Electroz and any material the customer supplies. When the customer agrees, it becomes a service job.',
    flow: {
      map: QUOTATION_STATUS,
      steps: ['draft', 'sent', 'approved', 'converted'],
      side: [
        { value: 'rejected', note: 'Customer said no' },
        { value: 'expired', note: 'Valid-till date passed' },
      ],
    },
    tables: [
      {
        title: 'Quotation status',
        map: QUOTATION_STATUS,
        badges: true,
        rows: [
          { value: 'draft', meaning: 'Being prepared. Not shared with the customer yet; everything can still be changed.', next: 'Share it, then mark it Sent.' },
          { value: 'sent', meaning: 'Shared with the customer (PDF, link or WhatsApp). Waiting for their answer.', next: 'Mark Accepted or Rejected when they reply.' },
          { value: 'approved', meaning: 'The customer agreed to the price.', next: 'Use “Create job” to turn it into a service job.' },
          { value: 'rejected', meaning: 'The customer said no.', next: 'Move it back to Draft to revise and send again.' },
          { value: 'expired', meaning: 'The valid-till date passed without an answer. The app marks this by itself every day.', next: 'Send it again, or revise it first.' },
          { value: 'converted', meaning: 'A service job was created from this quotation. Its status can no longer change.', next: 'Follow the work in Service jobs.' },
        ],
      },
    ],
    howItWorks: [
      'Create a quotation for a customer, or start from a quote template to save typing.',
      'Add fitting and labour lines, material from Electroz, and note any material the customer will supply.',
      'Share the PDF, a link or WhatsApp message, and mark the quotation Sent.',
      'When the customer agrees, mark it Accepted and use “Create job”. The job gets the customer, site and material list from the quotation.',
    ],
    tips: [
      'Quotation numbers have a random 6-digit part (QT/2026-27/482915), so customers cannot tell how many quotations you make.',
      'Use Duplicate to start a new quotation from an old one.',
      'Your signature (Settings) prints on quotations when it is switched on.',
    ],
  },
  {
    id: 'jobs',
    title: 'Service jobs',
    icon: 'wrench',
    to: '/jobs',
    roles: ALL,
    summary: 'The work done at a customer’s site. A job comes from an accepted quotation or is created directly, gets workers and a visit date, uses material from stock, and ends with an invoice.',
    flow: {
      map: JOB_STATUS,
      steps: ['pending', 'assigned', 'in_progress', 'completed', 'invoiced'],
      side: [
        { value: 'on_hold', note: 'Paused, resume later' },
        { value: 'cancelled', note: 'Job dropped' },
      ],
    },
    tables: [
      {
        title: 'Job status',
        map: JOB_STATUS,
        badges: true,
        rows: [
          { value: 'pending', meaning: 'The job is created but no worker has been given it yet.', next: 'Office assigns workers and a visit date.' },
          { value: 'assigned', meaning: 'One or more workers have the job. Work has not started.', next: 'The worker taps “Start work” on site.' },
          { value: 'in_progress', meaning: 'Work is going on at the site. Material can be issued from stock to the job.', next: 'Mark it Ready to invoice when the work is finished.' },
          { value: 'on_hold', meaning: 'Work is paused, for example waiting for material, the customer or a payment.', next: 'Resume to In progress (or Assigned) when ready.' },
          { value: 'completed', meaning: 'The work is finished but no invoice has been issued yet. These jobs are money waiting to be billed.', next: 'Office creates and issues the invoice. If more work is needed, move it back to In progress.' },
          { value: 'invoiced', meaning: 'The invoice for this job has been issued. The job is closed and cannot be changed.', next: 'Collect the payment on the invoice.' },
          { value: 'cancelled', meaning: 'The job was dropped. Material issued to it must be returned to stock first.', next: 'It can be reopened as Not assigned if needed.' },
        ],
      },
      {
        title: 'Priority',
        map: PRIORITY,
        badges: true,
        rows: [
          { value: 'low', meaning: 'Can wait; do it when there is time.' },
          { value: 'normal', meaning: 'The usual order of work.' },
          { value: 'high', meaning: 'Do it before normal jobs.' },
          { value: 'urgent', meaning: 'Needs attention today, for example a power failure at the site.' },
        ],
      },
    ],
    howItWorks: [
      'Create the job from an accepted quotation (“Create job”) or directly with New job.',
      'Assign one or more workers and a visit date. The job moves to Assigned.',
      'The worker starts work on site, adds photos, notes and the customer’s signature, and marks material as used.',
      'When the work is done, the job is marked Ready to invoice.',
      'The office creates the invoice from the job. When the invoice is issued, the job becomes Invoiced.',
    ],
    tips: [
      'Workers can only start, pause and finish their own jobs. Other changes are made by the office.',
      'The Work board shows jobs as columns by status; drag a card to move it.',
      'A job’s profit (Reports) is the invoice value minus the material and expenses linked to it.',
    ],
  },
  {
    id: 'invoices',
    title: 'Invoices',
    icon: 'receipt',
    to: '/invoices',
    roles: STAFF,
    summary: 'The bill for finished work. An invoice is prepared as a draft, then issued with its official number, and payments are recorded against it until it is paid.',
    flow: {
      map: INVOICE_STATUS,
      steps: ['draft', 'final'],
      side: [{ value: 'cancelled', note: 'Issued invoice withdrawn' }],
    },
    tables: [
      {
        title: 'Invoice status',
        map: INVOICE_STATUS,
        badges: true,
        rows: [
          { value: 'draft', meaning: 'Being prepared. It has no number yet, can be edited freely and can be deleted.', next: 'Use “Issue invoice” when it is correct.' },
          { value: 'final', meaning: 'Issued: it has its official number and is locked. Payments can now be recorded. For a job, the job must be Ready to invoice and becomes Invoiced.', next: 'Record payments as the customer pays.' },
          { value: 'cancelled', meaning: 'An issued invoice that was withdrawn, with a reason. Its payments must be reversed (or moved to an advance) first.', next: 'Make a new invoice if needed.' },
        ],
      },
      {
        title: 'Payment status (issued invoices)',
        map: PAYMENT_STATUS,
        badges: true,
        rows: [
          { value: 'unpaid', meaning: 'Nothing received yet.', next: 'Follow up; it shows in Money to collect.' },
          { value: 'partial', meaning: 'Some money received; a balance is still due.', next: 'Record the rest when it comes.' },
          { value: 'paid', meaning: 'The full amount is received.' },
        ],
      },
    ],
    howItWorks: [
      'Create an invoice from a job (or directly for a customer). It starts as a Draft.',
      'Check the lines and totals, then use “Issue invoice”. It gets its number and can no longer be edited.',
      'Share the PDF or link with the customer.',
      'Record each payment received. The payment status moves from Unpaid to Partly paid to Paid by itself.',
    ],
    tips: [
      'A GST invoice prints as “TAX INVOICE” with CGST and SGST; a non-GST invoice prints without tax.',
      'An invoice is overdue when its due date has passed and money is still due. The dashboard shows these under Money to collect.',
      'Your signature prints on invoices when it is switched on in Settings.',
    ],
  },
  {
    id: 'payments',
    title: 'Payments',
    icon: 'swap',
    to: '/transactions',
    roles: STAFF,
    summary: 'Every rupee that comes in or goes out: customer payments, supplier payments, expenses, salaries and advances. Cash, bank and UPI balances are worked out from these entries.',
    tables: [
      {
        title: 'Payment types',
        map: TXN_CATEGORIES,
        rows: [
          { value: 'invoice_payment', meaning: 'Money received from a customer against an issued invoice.' },
          { value: 'advance', meaning: 'Money received from a customer before an invoice; it can be used against an invoice later.' },
          { value: 'purchase_payment', meaning: 'Money paid to a supplier against a purchase bill.' },
          { value: 'expense', meaning: 'Money paid for an expense such as fuel, rent or tools.' },
          { value: 'refund', meaning: 'Money returned to a customer.' },
          { value: 'salary', meaning: 'Wages paid to a team member, usually against a salary slip.' },
          { value: 'staff_advance', meaning: 'Money given to a team member ahead of salary. It is recovered from a later salary slip.' },
          { value: 'capital', meaning: 'Money the owner put into the business.' },
          { value: 'withdrawal', meaning: 'Money the owner took out of the business.' },
          { value: 'other', meaning: 'Anything that does not fit above.' },
        ],
      },
    ],
    howItWorks: [
      'Record a payment from the invoice, purchase, expense or salary slip it belongs to, or from Payments directly.',
      'Choose how it was paid: cash, UPI, bank transfer, cheque or card.',
      'A wrong payment is never deleted. Reverse it instead: the app adds an opposite entry, so the history stays complete.',
    ],
  },
  {
    id: 'purchases',
    title: 'Purchases & stock',
    icon: 'cart',
    to: '/purchases',
    roles: STAFF,
    summary: 'Supplier bills for material. Material bought for the store goes into stock; material bought for a job goes straight to that job. Stock goes down when material is issued to jobs.',
    tables: [
      {
        title: 'Purchase status',
        map: RECORD_STATUS,
        badges: true,
        rows: [
          { value: 'active', meaning: 'A recorded supplier bill. Its material is in stock (or on the job).' },
          { value: 'cancelled', meaning: 'The bill was withdrawn: its material is taken back out of stock. Payments to the supplier must be reversed first.' },
        ],
      },
      {
        title: 'Payment status (supplier bills)',
        map: PAYMENT_STATUS,
        badges: true,
        rows: [
          { value: 'unpaid', meaning: 'Nothing paid to the supplier yet.' },
          { value: 'partial', meaning: 'Some of the bill is paid.' },
          { value: 'paid', meaning: 'The whole bill is paid.' },
        ],
      },
      {
        title: 'Why stock changed',
        map: STOCK_REASONS,
        rows: [
          { value: 'opening', meaning: 'Stock you already had when you started using the app.' },
          { value: 'purchase', meaning: 'Added by a supplier bill.' },
          { value: 'purchase_cancelled', meaning: 'Removed because its supplier bill was cancelled.' },
          { value: 'issued_to_service', meaning: 'Taken from the store to a job.' },
          { value: 'returned_from_site', meaning: 'Unused material brought back from a job.' },
          { value: 'damaged', meaning: 'Broken or lost material written off. It counts as a cost in profit & loss.' },
          { value: 'adjustment', meaning: 'A correction after counting the store.' },
        ],
      },
    ],
    howItWorks: [
      'Record the supplier bill with its items, and attach a photo of the bill.',
      'Choose whether the material is for stock or for a particular job.',
      'Stock and the average cost of each material update by themselves.',
      'Issue material from stock to a job; return what was not used.',
    ],
    tips: ['A material is “low on stock” when its quantity is at or below its minimum level. The dashboard lists these.'],
  },
  {
    id: 'expenses',
    title: 'Expenses',
    icon: 'moneyOut',
    to: '/expenses',
    roles: STAFF,
    summary: 'Running costs that are not material purchases: fuel, rent, tools, travel and so on. Categories are set in Master data.',
    tables: [
      {
        title: 'Expense status',
        map: RECORD_STATUS,
        badges: true,
        rows: [
          { value: 'active', meaning: 'A recorded expense. It counts in reports and profit & loss.' },
          { value: 'cancelled', meaning: 'Withdrawn, with a reason. It no longer counts anywhere.' },
        ],
      },
    ],
    howItWorks: [
      'Record the expense with its category, amount, GST (if any) and how it was paid.',
      'Link it to a job when the cost belongs to that job, so the job’s profit is correct.',
    ],
  },
  {
    id: 'salary',
    title: 'Attendance & salary',
    icon: 'calendar',
    to: '/payroll',
    roles: STAFF,
    summary: 'Mark who worked each day, then make salary slips from attendance. Advances given to staff are recovered from their slips.',
    flow: {
      map: PAYMENT_STATUS,
      steps: ['unpaid', 'partial', 'paid'],
    },
    tables: [
      {
        title: 'Attendance marks',
        map: ATTENDANCE,
        badges: true,
        rows: [
          { value: 'present', meaning: 'Worked the full day. Counts as one paid day.' },
          { value: 'half_day', meaning: 'Worked half the day. Counts as half a paid day.' },
          { value: 'absent', meaning: 'Did not work. Not paid (a monthly salary is reduced for the day).' },
          { value: 'holiday', meaning: 'Weekly off or a holiday. Paid for people on a monthly salary, not for daily wage.' },
        ],
      },
      {
        title: 'How a person is paid',
        map: PAY_TYPES,
        rows: [
          { value: 'daily', meaning: 'Paid for the days actually worked: daily wage × (present days + half of the half days).' },
          { value: 'monthly', meaning: 'A fixed salary for the month. Absent days (and half of each half day) are cut; offs and unmarked days are paid. One slip covers one calendar month.' },
        ],
      },
      {
        title: 'Salary slip payment',
        map: PAYMENT_STATUS,
        badges: true,
        rows: [
          { value: 'unpaid', meaning: 'The slip is made but no salary has been paid against it.' },
          { value: 'partial', meaning: 'Part of the salary is paid.' },
          { value: 'paid', meaning: 'The full net pay is paid.' },
        ],
      },
    ],
    howItWorks: [
      'Mark attendance every day on the Attendance screen.',
      'At the end of the pay period, create a salary slip. The app works out the pay from attendance, the person’s pay type and any overtime hours.',
      'Add bonus or deductions, and recover part of any advance the person owes.',
      'Pay the salary against the slip; its payment status updates by itself.',
    ],
    tips: [
      'Days covered by a salary slip are locked in attendance, so paid days cannot change by mistake. Cancel the slip to change them.',
      'Staff advances shows how much each person still owes.',
    ],
  },
];
